import { ElementRef } from '@angular/core';
import { DateUtility } from '../../classes/dateUtility';
import { COLORS } from '../../interfaces/constants';
import { IHighChartCustomSeries } from '../available-water-chart/interfaces';
import highChartExportOptions from '../shared/highchartsExportOptions';
import { AppliedFertilizerChartDataSet } from './interfaces';
import * as Highcharts from 'highcharts';

export class MineralizationChartService {
	private _chartData: Highcharts.IndividualSeriesOptions[];
	private min: number; // min max date number values for displaying the chart
	private max: number;

	constructor(
		public chartContainer: ElementRef
	) {}

	public convertToGraphData(records: AppliedFertilizerChartDataSet): void {

		let cumulativeN: number;

		if (!records || !records.MineralizedN || records.MineralizedN.length === 0) {
			return;
		}

		cumulativeN = 0;

		let temp: {
			cumulativeN: Highcharts.LineChartSeriesOptions,
			mineralizedN: Highcharts.LineChartSeriesOptions,
			NAssimilated: Highcharts.LineChartSeriesOptions,
			mineralNPool: Highcharts.LineChartSeriesOptions
			decompositionRate: Highcharts.LineChartSeriesOptions,
			TemperatureFactor: Highcharts.LineChartSeriesOptions
		};

		temp = {
			cumulativeN: {
				name: 'Cumulative N Mineralization',
				visible: true,
				type: 'line',
				data: [],
				tooltip: {
					valueSuffix: ' lbs N/acre'
				},
				marker: {
					enabled: false
				}
			},
			mineralizedN: {
				name: 'Net Daily N Mineralization',
				visible: false,
				type: 'line',
				data: [],
				tooltip: {
					valueSuffix: ' lbs N/acre'
				},
				marker: {
					enabled: false
				}
			},
			NAssimilated: {
				name: 'Daily N Assimilated by Microbes',
				visible: false,
				type: 'line',
				data: [],
				pointWidth: 5,
				tooltip: {
					valueSuffix: ' lbs N/acre'
				},
				marker: {
					enabled: false
				}
			},
			mineralNPool: {
				name: 'Soil N pool',
				visible: false,
				type: 'line',
				data: [],
				tooltip: {
					valueSuffix: ' lbs N/acre'
				},
				marker: {
					enabled: false
				}
			},
			decompositionRate: {
				name: 'Decomposition Rate',
				visible: false,
				type: 'line',
				data: [],
				tooltip: {
					valueSuffix: ''
				},
				marker: {
					enabled: false
				}
			},
			TemperatureFactor: {
				name: 'Temperature Factor',
				visible: false,
				type: 'line',
				data: [],
				tooltip: {
					valueSuffix: ''
				},
				marker: {
					enabled: false
				}
			},
		}

		for (let record of records.MineralizedN) {
			let utc: number;

			utc = DateUtility.DateToUTC(DateUtility.DotNetToDate(record.EventDate));
			temp.mineralizedN.data.push([utc, record.Amount]);
			cumulativeN += record.Amount;
			temp.cumulativeN.data.push([utc, cumulativeN]);
		}

		for (let record of records.NAssimilated) {
			let utc: number;

			utc = DateUtility.DateToUTC(DateUtility.DotNetToDate(record.EventDate));
			temp.NAssimilated.data.push([utc, record.Amount]);
		}

		for (let record of records.MineralNPool) {
			let utc: number;

			utc = DateUtility.DateToUTC(DateUtility.DotNetToDate(record.EventDate));
			temp.mineralNPool.data.push([utc, record.Amount]);
		}

		for (let record of records.DecompositionRate) {
			let utc: number;

			utc = DateUtility.DateToUTC(DateUtility.DotNetToDate(record.EventDate));
			temp.decompositionRate.data.push([utc, record.Amount]);
		}

		for (let record of records.TemperatureFactor) {
			let utc: number;

			utc = DateUtility.DateToUTC(DateUtility.DotNetToDate(record.EventDate));
			temp.TemperatureFactor.data.push([utc, record.Amount]);
		}

		const { minDate, maxDate } = records.MineralizedN.reduce((acc, curr) => {
			let date: number;

			date = DateUtility.DateToUTC(DateUtility.DotNetToDate(curr.EventDate));

			return {
				minDate: date < acc.minDate ? date : acc.minDate,
				maxDate: date > acc.maxDate ? date : acc.maxDate
			};
		}, { minDate: Infinity, maxDate: -Infinity });

		this.min = minDate;
		this.max = maxDate;

		this._chartData = [];

		this._chartData.push(temp.cumulativeN);
		this._chartData.push(temp.mineralizedN);
		this._chartData.push(temp.NAssimilated);
		this._chartData.push(temp.mineralNPool);
		this._chartData.push(temp.decompositionRate);
		this._chartData.push(temp.TemperatureFactor);
	}

	public drawChart(): Highcharts.ChartObject {
		let options: Highcharts.Options;

		if (!this.chartContainer) {
			return;
		}

		options = {
			chart: {
			// type: 'line',
				width: 733,
				height: 320,
				spacingBottom: 5,
			},
			title: {
				text: null,
			},
			legend: {
				margin: 6,
				title: {
					text: null,
				}
			},
			tooltip: {
				backgroundColor: COLORS.WHITE,
				borderColor: COLORS.WHITE,
				valueDecimals: 3,
				valueSuffix: 'lbs N/acre',
			},
			xAxis: {
				type: 'datetime',
				gridLineWidth: 1,
				min: this.min,
				max: this.max,
				plotLines: []
			},
			yAxis: [{
				title: {
					text: 'Lbs N/acre'
				}
			}],
			series: this._chartData,
			exporting: highChartExportOptions,
			credits: {
				enabled: false
			}
		}

		return Highcharts.chart(this.chartContainer.nativeElement, options);
	}
}
